import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';
import type {IHarcMasraf} from '@/plugins/uyap-plugin/uyap/CommonInterfaces';
import UyapTakipAc from '@/plugins/uyap-plugin/takip_ac/UyapTakipAc';
import MtsTakipAc from '@/plugins/uyap-plugin/takip_ac/MtsTakipAc';

export interface TakipHazirlaExcelRow {
  'Dosya Esas No': string;
  'İcra Dairesi': string;
  Alacaklı: string;
  Borçlu: string;
  Mesaj: string;
  'Takibe Esas Tutar': string;
  'Harc + Masraf': string;
  'Harc Masraf Toplamı': string;
}

const TARAF_SEPARATOR = '; ';

type Dosya = UyapTakipAc | MtsTakipAc;

function formatTl(n: number | undefined | null): string {
  if (n == null || Number.isNaN(Number(n))) {
    return '-';
  }
  return (
    Number(n).toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ' TL'
  );
}

function formatTarafNames(
  tarafList: Array<{isAlacakli?: () => boolean; isBorclu?: () => boolean; getIsim: () => string}> | undefined,
  pick: (t: {isAlacakli?: () => boolean; isBorclu?: () => boolean; getIsim: () => string}) => boolean
): string {
  if (!tarafList || !tarafList.length) {
    return '';
  }
  return tarafList
    .filter(t => pick(t))
    .map(t => (t.getIsim?.() || '').trim())
    .filter(Boolean)
    .join(TARAF_SEPARATOR);
}

function harcSatirTutari(item: IHarcMasraf): number {
  const raw = item.hesapMiktar ?? item.harcMasrafBedel;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function buildDosyaEsasNo(dosya: Dosya): string {
  const esas = (dosya.takipBilgileri?.dosyaEsasNo || '').trim();
  if (esas) return esas;
  const dosyaNo = (dosya.dosyaBilgileri?.dosyaNo || '').trim();
  return dosyaNo || '-';
}

function buildIcraDairesiHucre(dosya: Dosya): string {
  const icra = (dosya.takipBilgileri?.icraDairesi || '').trim();
  if (icra) return icra;
  if (dosya instanceof UyapTakipAc) {
    const tevziBirim = (dosya.tevziBilgileri?.birimAdi || '').trim();
    if (tevziBirim) return tevziBirim;
  }
  const uyapBirim = (dosya.dosyaBilgileri?.birimAdi || '').trim();
  return uyapBirim || '-';
}

function getHarcMasrafSatirlari(dosya: Dosya): IHarcMasraf[] {
  if (dosya instanceof UyapTakipAc) {
    if (dosya.harcMasrafList && dosya.harcMasrafList.length > 0) {
      return dosya.harcMasrafList;
    }
  } else {
    const mtsList = dosya.mtsHarcBilgileri?.selectedTarafHashList;
    if (mtsList && mtsList.length > 0) {
      return mtsList as IHarcMasraf[];
    }
  }
  return [];
}

function buildHarcMasrafDetay(dosya: Dosya): string {
  const list = getHarcMasrafSatirlari(dosya);
  if (list.length > 0) {
    return list
      .map(item => {
        const ad = (item.harcMasrafAdi || '').trim() || 'Kalem';
        const tutar = formatTl(harcSatirTutari(item));
        const harcKodu = (item.harcMasraf || '').toUpperCase();
        const tip =
          harcKodu === 'D'
            ? 'Masraf'
            : harcKodu === 'H'
              ? 'Harc'
              : (item.harcMasrafAdi || '').toLowerCase().includes('masraf')
                ? 'Masraf'
                : 'Harc';
        return `${ad}: ${tutar} (${tip})`;
      })
      .join('; ');
  }
  if (dosya instanceof UyapTakipAc) {
    const h = dosya.harcMiktar;
    const m = dosya.masrafMiktar;
    if ((h != null && Number(h) !== 0) || (m != null && Number(m) !== 0)) {
      const parts: string[] = [];
      if (h != null && Number(h) !== 0) parts.push(`Harc: ${formatTl(h)}`);
      if (m != null && Number(m) !== 0) parts.push(`Masraf: ${formatTl(m)}`);
      return parts.join('; ');
    }
  }
  return '-';
}

function buildHarcMasrafToplamHucre(dosya: Dosya): string {
  const stored =
    dosya instanceof UyapTakipAc
      ? (dosya.harcMasrafToplam ?? dosya.parsedFile?.harcMasrafToplam)
      : dosya.parsedFile?.harcMasrafToplam;
  if (stored != null && Number(stored) !== 0) {
    return formatTl(stored);
  }
  const list = getHarcMasrafSatirlari(dosya);
  if (list.length > 0) {
    const sum = list.reduce((acc, i) => acc + harcSatirTutari(i), 0);
    if (sum !== 0) return formatTl(sum);
  }
  return '-';
}

function buildMesajText(dosya: Dosya): string {
  const texts = (dosya.messages || [])
    .map(entry => (entry.message || '').trim())
    .filter(t => t.length > 0 && t !== 'Bekleniyor!');
  if (!texts.length) return '-';
  return [...new Set(texts)].join(' | ');
}

export function buildTakipHazirlaExcelRows(
  dosyalar: Dosya[]
): TakipHazirlaExcelRow[] {
  return dosyalar.map(dosya => {
    const tarafList = dosya.parsedFile?.tarafList;
    const alacakli = formatTarafNames(tarafList, t => !!t.isAlacakli?.());
    const borclu = formatTarafNames(tarafList, t => !!t.isBorclu?.());
    const tutar = dosya.parsedFile
      ? dosya.parsedFile.getToplamTakibeEsasMiktarText()
      : '-';

    return {
      'Dosya Esas No': buildDosyaEsasNo(dosya),
      'İcra Dairesi': buildIcraDairesiHucre(dosya),
      Alacaklı: alacakli || '-',
      Borçlu: borclu || '-',
      Mesaj: buildMesajText(dosya),
      'Takibe Esas Tutar': (tutar && String(tutar).trim()) || '-',
      'Harc + Masraf': buildHarcMasrafDetay(dosya),
      'Harc Masraf Toplamı': buildHarcMasrafToplamHucre(dosya),
    };
  });
}

function buildExcelFileName(): string {
  const now = new Date();
  const fmt = new Intl.DateTimeFormat('tr-TR', {
    timeZone: 'Europe/Istanbul',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(now);
  const safe = fmt.replace(':', '-');
  return `E-İcrapro Takip Hazırla (${safe}).xlsx`;
}

export interface DownloadTakipHazirlaExcelOptions {
  dosyalar: Dosya[];
  sheetName?: string;
}

/**
 * Yüklenen takip satırlarını .xlsx olarak indirir.
 * @returns indirme yapıldıysa true, dosya yoksa false
 */
export function downloadTakipHazirlaExcel(
  options: DownloadTakipHazirlaExcelOptions
): boolean {
  const {dosyalar, sheetName = 'Takipler'} = options;
  if (!dosyalar.length) {
    return false;
  }
  const rows = buildTakipHazirlaExcelRows(dosyalar);
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31));
  const buffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, buildExcelFileName());
  return true;
}
